// One vocabulary for the ACA module. Every tooltip reads from here so the
// same term is explained the same way on every tab. Keep entries short,
// concrete and in the employer's language; cite the rule, not the code.

export interface GlossaryEntry {
  /** Short label used where the term appears as a heading. */
  label: string;
  /** Two to four sentences. First sentence = the definition. */
  text: string;
}

export const ACA_GLOSSARY = {
  // ---- who is who -----------------------------------------------------------
  ale: {
    label: "Applicable Large Employer (ALE)",
    text: "An employer that averaged 50 or more full-time employees plus full-time equivalents per month over the previous calendar year. Only ALEs must offer coverage and file Forms 1094-C and 1095-C. Status for a year is decided by the prior year's workforce, never by current headcount.",
  },
  aleAverage: {
    label: "Average FT + FTE",
    text: "For each month of the prior year: ACA full-time employees plus full-time equivalents. The twelve monthly totals are averaged and rounded down. 50 or more means ALE. A workforce that shrank in December is still judged on the whole year.",
  },
  aleBasisYear: {
    label: "ALE basis year",
    text: "The calendar year whose workforce decides ALE status. Status for 2026 comes from the 2025 workforce, month by month.",
  },
  aleEstimated: {
    label: "Estimated",
    text: "Some or all months had no recorded hours of service, so hours were estimated from scheduled weekly hours. The determination is provisional until actual monthly hours are recorded, ideally from payroll.",
  },
  aleSourceFrozen: {
    label: "Frozen year-end snapshot",
    text: "The ALE figure comes from the workforce snapshot frozen in January for the prior calendar year. Later edits to employees, hours or dates do not change it. Re-freezing requires a reason and is audited.",
  },
  aleSourceLive: {
    label: "Live recomputation",
    text: "No frozen snapshot exists for the prior year, so the ALE figure was recomputed from current data. It can change whenever prior-year data is edited. Take a year-end snapshot on the History tab to freeze it.",
  },
  aleOverride: {
    label: "ALE override",
    text: "An audited manual decision that replaces the calculated ALE status, for example when a controlled group makes the employer an ALE although this company alone is under 50. Records who, when and why.",
  },
  aggregatedAleGroup: {
    label: "Aggregated ALE group",
    text: "Companies under common ownership or control are treated as one employer for the 50-employee test. Each member files its own 1094-C and lists the other members in Part IV. Group-wide headcount cannot be computed from one member's data, so confirm group ALE status with an audited override.",
  },
  fte: {
    label: "Full-time equivalent (FTE)",
    text: "Part-time hours converted into whole employees. For each month, add every non-full-time employee's hours of service, counting at most 120 per person, and divide by 120. FTEs count toward the 50 threshold but do not receive a 1095-C for being FTEs.",
  },
  fteCap: {
    label: "120-hour cap",
    text: "When computing FTEs, no non-full-time employee contributes more than 120 hours in a month, even if they worked more. This cap is set by the regulations.",
  },

  // ---- hours and status -----------------------------------------------------
  fullTime: {
    label: "ACA full-time",
    text: "130 or more hours of service in a calendar month, the monthly equivalent of 30 hours a week. Determined month by month from hours, not from the job title or the employer's own full-time or part-time label.",
  },
  partTime: {
    label: "Part-time (ACA)",
    text: "Fewer than 130 hours of service in the month. Part-time employees count toward FTEs for the ALE test and generally do not need a 1095-C unless they enrolled in self-insured coverage.",
  },
  hoursOfService: {
    label: "Hours of service",
    text: "Hours the employee is paid or entitled to pay for: work plus paid leave such as vacation, holiday, illness, disability, jury duty and military duty. Recorded per employee per month. Payroll is the defensible source.",
  },
  hoursEstimated: {
    label: "Estimated from schedule",
    text: "No hours were recorded for this month, so scheduled weekly hours were converted to a monthly figure (weekly x 52 / 12) and prorated for a partial month. Good enough to preview, not to file. Record actual hours to replace the estimate.",
  },
  hoursMissing: {
    label: "Hours missing",
    text: "No recorded hours and no scheduled weekly hours, so full-time status cannot be determined for this month. This blocks form generation until hours are entered.",
  },
  measuring: {
    label: "Measuring",
    text: "A new variable-hour, seasonal or part-time hire whose initial measurement period has not ended yet. Their status is not assessed until the period is measured.",
  },
  monthlyMeasurement: {
    label: "Monthly measurement method",
    text: "Full-time status is decided each calendar month from that month's hours: 130 or more means full-time. Simple and immediate, but an employee can flip between full-time and part-time month to month.",
  },
  lookBack: {
    label: "Look-back measurement method",
    text: "Hours are averaged over a measurement period of 3 to 12 months; the result then fixes the employee's status for the following stability period regardless of hours worked in it. Gives stable, predictable status for variable-hour workforces.",
  },
  standardMeasurementPeriod: {
    label: "Standard measurement period",
    text: "The 3 to 12 month window over which an ongoing employee's hours are averaged under the look-back method. 130 hours a month or more on average makes the employee full-time for the whole following stability period.",
  },
  administrativePeriod: {
    label: "Administrative period",
    text: "Up to 90 days between the end of a measurement period and the start of the stability period, used to calculate results and run enrollment. It cannot create a gap in coverage for employees already in a stability period.",
  },
  stabilityPeriod: {
    label: "Stability period",
    text: "The period, at least 6 months and at least as long as the measurement period, during which the status decided by the measurement period is locked in. An employee measured as full-time must be treated as full-time for the entire stability period.",
  },
  initialMeasurementPeriod: {
    label: "Initial measurement period",
    text: "The employee-specific measurement period for a new variable-hour, seasonal or part-time hire, starting at or shortly after hire and lasting 3 to 12 months. Together with its administrative period it must end by the last day of the 13th full month after hire. Months in it are a non-assessment period.",
  },
  employeeClassification: {
    label: "Employee classification",
    text: "Under look-back, a new hire's Employment Type on the employee record decides the path: Variable Hour, Seasonal and Part-Time get an initial measurement period; Full-Time is expected to be full-time and is measured monthly until a standard measurement period completes.",
  },

  // ---- non-assessment ------------------------------------------------------
  lnap: {
    label: "Limited Non-Assessment Period",
    text: "A month in which the employer is not penalized for a full-time employee even though no coverage was offered, reported as Line 16 code 2D. Reasons: the first partial month of employment, the first three full months while a new full-time hire waits for coverage, an initial measurement period, or January to March of the employer's first year as an ALE.",
  },
  firstPartialMonth: {
    label: "First partial month",
    text: "When an employee starts on any day other than the 1st, that month is a non-assessment period. Coverage does not have to be offered for it.",
  },
  waitingPeriodLnap: {
    label: "Waiting period (new full-time hire)",
    text: "The first three full calendar months after hire are a non-assessment period, provided coverage is offered by the first day of the fourth full month. If the offer comes later, the relief does not apply and those months show as full-time with no offer.",
  },
  firstAleYear: {
    label: "First year as an ALE",
    text: "January through March of the first year an employer is an ALE are a non-assessment period for employees who were not offered coverage the prior year, as long as coverage is offered by April 1. Set the first ALE year on the Settings tab to enable it.",
  },
  waitingPeriod: {
    label: "Waiting period",
    text: "How long after hire an employee must wait before coverage can start, taken from the plan's eligibility group in the Benefits module. The ACA caps it at 90 days. Not edited here.",
  },
  entryDate: {
    label: "Coverage entry date",
    text: "When coverage actually begins once the waiting period ends: immediately, the first of the month, or the first of the following month. Taken from the plan's eligibility group. The engine treats coverage as offered for a month only if it was available every day of that month.",
  },

  // ---- offers and codes ----------------------------------------------------
  offer: {
    label: "Offer of coverage",
    text: "The employee could have enrolled in a health plan for every day of the month: eligible under the plan's eligibility group, past the waiting period, and the plan year in force. An offer for part of a month does not count for that month.",
  },
  mec: {
    label: "Minimum essential coverage (MEC)",
    text: "Coverage that satisfies the individual mandate: employer group health plans, government programs and marketplace plans. Nearly every group health plan qualifies. Dental, vision and other excepted benefits do not.",
  },
  minimumValue: {
    label: "Minimum value (MV)",
    text: "The plan pays at least 60% of the total allowed cost of benefits and covers hospital and physician services. Any bronze, silver, gold or platinum plan provides minimum value. Set in the plan library.",
  },
  qualifyingOffer: {
    label: "Qualifying Offer (1A)",
    text: "Minimum-value coverage offered to the employee at a self-only cost at or below the federal poverty line safe harbor amount, with at least minimum essential coverage offered to spouse and dependents. Line 15 is left blank and no safe-harbor code is needed.",
  },
  line14: {
    label: "Line 14: Offer of Coverage",
    text: "A code for each month describing what was offered. 1A qualifying offer; 1B employee only; 1C employee and dependents; 1D employee and spouse; 1E employee, spouse and dependents; 1F coverage that is not minimum value; 1H no offer; 1G annual code for a never-full-time employee enrolled in self-insured coverage.",
  },
  line15: {
    label: "Line 15: Employee Required Contribution",
    text: "The employee's monthly share of the lowest-cost self-only minimum-value plan offered, after the employer's contribution. Reported only for codes 1B, 1C, 1D, 1E, 1J and 1K, and shown as 0.00 when the employer pays it all. It is the amount tested for affordability, not what the employee actually pays for the plan they chose.",
  },
  line16: {
    label: "Line 16: Safe Harbor and Other Relief",
    text: "Why no penalty applies for the month. One code at most, in this order: 2C enrolled; 2A not employed; 2D non-assessment period; 2B not full-time; 2F W-2, 2G federal poverty line or 2H rate of pay affordability safe harbor. Left blank when none applies, which flags potential exposure.",
  },
  line16Blank: {
    label: "Line 16 blank",
    text: "No relief code applies: the employee was full-time, was offered coverage or should have been, did not enroll, and the offer was not affordable under the chosen safe harbor (or no coverage was offered). This is where employer penalty exposure lives.",
  },
  lowestCostPlan: {
    label: "Lowest-cost MV self-only plan",
    text: "Among the minimum-value plans this employee was eligible for in the month, the one with the lowest employee-only cost after employer contribution. Determined per employee, because eligibility groups, waiting periods, age-rated premiums and contributions differ.",
  },
  employeeRequiredContribution: {
    label: "Employee required contribution",
    text: "What the employee would have to pay per month for the lowest-cost self-only minimum-value plan available to them, after the employer contribution, floored at $0. Derived from plan rates and contribution rules; an audited override exists for exceptions.",
  },
  planStartMonth: {
    label: "Plan start month",
    text: "The two-digit month in which the plan year of the coverage offered begins (for example 10 for an October renewal). Required on Form 1095-C. 00 means no coverage was offered to this employee in the year.",
  },
  formRequired: {
    label: "1095-C required",
    text: "A 1095-C must be furnished to every employee who was ACA full-time in at least one month, and, for self-insured coverage, to anyone enrolled even if never full-time (reported with annual code 1G).",
  },

  // ---- affordability -------------------------------------------------------
  affordability: {
    label: "Affordability",
    text: "Coverage is affordable for a month when the employee required contribution is at or below the threshold produced by the chosen safe harbor. Affordable offers earn a Line 16 safe-harbor code; unaffordable ones leave it blank.",
  },
  affordabilityPercent: {
    label: "Affordability percentage",
    text: "The IRS percentage of income above which self-only coverage is unaffordable: 8.39% for plan years starting in 2024, 9.02% in 2025, 9.96% in 2026. It follows the plan year's start date, so a non-calendar plan year can use two percentages within one tax year. Maintained by Administr, not editable.",
  },
  safeHarbor: {
    label: "Affordability safe harbor",
    text: "Because employers cannot know household income, the IRS allows three proxies: federal poverty line, rate of pay or Form W-2 wages. The employer picks one and must apply it consistently within a reasonable category of employees.",
  },
  fplSafeHarbor: {
    label: "Federal poverty line safe harbor",
    text: "Affordable if the monthly employee contribution is at or below (FPL for one person / 12) x the affordability percentage. Uses the FPL in effect within six months before the plan year starts. Needs no wage data and qualifies for code 1A.",
  },
  fpl: {
    label: "Federal poverty line (FPL)",
    text: "The HHS poverty guideline for a one-person household in the 48 contiguous states, published each January. Employers may use any guideline in effect within six months before the plan year begins; Administr uses the lowest eligible one, which is the usual choice.",
  },
  rateOfPaySafeHarbor: {
    label: "Rate of pay safe harbor",
    text: "Hourly employees: the lowest hourly rate during the month x 130 x the affordability percentage. Salaried employees: monthly salary x the percentage, but not available if salary was cut during the year. Uses the effective-dated compensation on the employee record.",
  },
  w2SafeHarbor: {
    label: "Form W-2 safe harbor",
    text: "Affordable if the annual required contribution for the months offered does not exceed Box 1 wages x the affordability percentage, prorated for partial-year employment. Because Box 1 is known only at year end, it shows Pending Data until final wages are entered.",
  },
  pendingData: {
    label: "Pending data",
    text: "Affordability cannot be decided yet because an input is missing: W-2 wages not final, no compensation on record, or the required contribution could not be priced. Never treated as affordable or unaffordable.",
  },
  notApplicable: {
    label: "Not applicable",
    text: "No affordability test for the month: the employee was not employed, not offered coverage, or in a non-assessment period.",
  },
  safeHarborCategory: {
    label: "Employee category",
    text: "A safe harbor may be assigned to a reasonable category such as salaried versus hourly, an employee class or a location, and must then be applied to everyone in it. Employees matching no category policy use the default policy.",
  },

  // ---- forms ---------------------------------------------------------------
  form1095c: {
    label: "Form 1095-C",
    text: "The employee statement: Part I identifies employee and employer, Part II gives the monthly offer, contribution and relief codes (Lines 14 to 16) and the plan start month, Part III lists covered individuals for self-insured coverage. Furnished to the employee and filed with the IRS.",
  },
  form1094c: {
    label: "Form 1094-C",
    text: "The employer transmittal that accompanies the 1095-Cs: employer identity, ALE status, the number of 1095-Cs, monthly full-time and total employee counts, whether minimum essential coverage was offered, and aggregated group members.",
  },
  line18: {
    label: "Line 18",
    text: "The number of 1095-C forms submitted with this transmittal. It must equal the count of generated employee forms, which the readiness check verifies.",
  },
  mecOfferIndicator: {
    label: "MEC offer indicator (95%)",
    text: "Checked for a month when minimum essential coverage was offered to at least 95% of full-time employees (excluding those in a non-assessment period) and their dependents. Falling short in any month exposes the employer to the larger of the two ACA penalties.",
  },
  ftCount1094: {
    label: "Full-time employee count (1094-C)",
    text: "Employees who were ACA full-time in the month, excluding anyone in a Limited Non-Assessment Period. This is a different number from the ALE test, which includes everyone.",
  },
  totalCount1094: {
    label: "Total employee count (1094-C)",
    text: "Every employee, full-time or not, counted on the same day each month. The IRS allows the first day, the last day, or the 12th, chosen on the Settings tab and applied consistently for the year.",
  },
  employeeCountDay: {
    label: "Employee count day",
    text: "The day of each month on which total employees are counted for Form 1094-C: first day, last day, or the 12th. Any is permitted; the same one must be used all year.",
  },
  partIII: {
    label: "Part III: Covered individuals",
    text: "For self-insured (including level-funded) coverage only: each covered person, employee and dependents, with SSN or date of birth, and the months they were covered for at least one day. Fully insured employers leave it blank because the carrier files Form 1095-B.",
  },
  selfInsured: {
    label: "Self-insured / level-funded",
    text: "The employer bears the claims risk. For ACA reporting this means Part III must list covered individuals, and part-time employees who enrolled also receive a 1095-C. Set per plan in the plan library.",
  },
  fullyInsured: {
    label: "Fully insured",
    text: "An insurance carrier bears the claims risk and reports covered individuals on Form 1095-B. The employer's 1095-C leaves Part III blank.",
  },
  code1G: {
    label: "Code 1G",
    text: "Used for the whole year, in the All 12 Months box, for an employee who was never ACA full-time but enrolled in the employer's self-insured coverage. Lines 15 and 16 are left blank.",
  },
  override: {
    label: "Audited override",
    text: "A manual correction to a calculated code or count, recorded with who made it, when, why, the prior value and the new value. The year is recalculated with the override applied. Overrides never rewrite the underlying data.",
  },
  readiness: {
    label: "Filing readiness",
    text: "Checks run before forms are generated: employer EIN and address, employee SSN and address, hours for every month, an affordability policy, plan minimum-value status, Line 14/15 consistency, and 1094-C counts. Blocking items stop generation; warnings do not.",
  },
  exceptionBlocking: {
    label: "Blocking exception",
    text: "Missing or contradictory source data that would make a form wrong. Administr reports it instead of assuming a code. Forms cannot be generated until it is fixed.",
  },
  exceptionWarning: {
    label: "Warning",
    text: "Something that does not stop form generation but signals risk or provisional data: estimated hours, a full-time employee with no offer, an unaffordable offer, pending W-2 wages.",
  },
  finalize: {
    label: "Finalize year",
    text: "Freezes the calculation and the generated forms as a numbered version. Later edits to HR or benefits data do not change a finalized year. Unlocking and regenerating creates a new version; the original is kept.",
  },
  snapshotVersion: {
    label: "Calculation version",
    text: "Each recalculation of a draft year overwrites the draft; finalizing freezes it as a version. Corrections after finalizing produce the next version, so every set of forms ever generated stays on record.",
  },
  yearEndSnapshot: {
    label: "Year-end workforce snapshot",
    text: "A frozen copy of the roster for a calendar year, taken automatically in early January: employment periods, schedule, compensation, monthly hours and monthly full-time status for every employee. It is the basis for the next year's ALE status and never changes afterward.",
  },
  manualSnapshot: {
    label: "Manual workforce snapshot",
    text: "A frozen copy taken on demand for the record, for example at fiscal year end. Not used for ALE determination.",
  },
  filedExternally: {
    label: "Filed externally",
    text: "Administr generates forms but does not transmit them to the IRS. This date and note are your own record of the filing you made through another channel.",
  },
  copyYearSettings: {
    label: "Copy year settings",
    text: "Copies configuration only: the measurement policy (with dates shifted one year) and safe-harbor policies. Employees, hours, rates, contributions and codes are always recomputed for the new year.",
  },

  // ---- deadlines and periods ------------------------------------------------
  deadlineFurnish: {
    label: "Furnish to employees",
    text: "Employees must receive their 1095-C by January 31 plus the automatic 30-day extension, moved to the next business day. Computed each year; not a fixed date.",
  },
  deadlineEfile: {
    label: "Electronic filing",
    text: "Forms 1094-C and 1095-C must reach the IRS electronically by March 31, moved to the next business day. Electronic filing is mandatory for 10 or more returns.",
  },
  deadlinePaper: {
    label: "Paper filing",
    text: "Only employers filing fewer than 10 returns may file on paper, by February 28, moved to the next business day.",
  },
  taxYear: {
    label: "Tax year",
    text: "The calendar year being reported: forms for 2025 are furnished and filed in early 2026. All twelve monthly determinations refer to this calendar year.",
  },
  planYear: {
    label: "Plan year",
    text: "The twelve months a health plan runs, starting on its effective date. It sets which rates apply and which affordability percentage applies, and it can straddle two tax years.",
  },
  employmentPeriods: {
    label: "Employment periods",
    text: "Each continuous stint of employment with start and end dates. A rehire opens a new period instead of overwriting the original hire date, so the tax-year population can be rebuilt historically.",
  },
  compensationHistory: {
    label: "Compensation history",
    text: "Effective-dated pay changes. The rate of pay safe harbor reads the rate in effect for each month, so a mid-year raise or cut is applied to the right months.",
  },
  w2Wages: {
    label: "W-2 Box 1 wages",
    text: "Taxable wages for the tax year, the only input the W-2 safe harbor may use. Enter them once final; until then the safe harbor shows Pending Data.",
  },
  population: {
    label: "Population",
    text: "Everyone whose employment overlapped the tax year, rebuilt from employment history: hired on or before December 31 and not terminated before January 1. Includes people who left during the year.",
  },
  showYourWork: {
    label: "Show your work",
    text: "The engine's step-by-step explanation for each month: hours and their source, full-time status, non-assessment reason, plan offered, required contribution, safe-harbor threshold and the resulting codes.",
  },

  // added: policies/affordability/settings
  affordabilityMixed: {
    label: "Mixed",
    text: "Some months were affordable and others were not, typically because the lowest-cost plan available changed during the year or a raise/cut moved the rate-of-pay threshold. Each month is tested on its own; only the unaffordable months carry penalty exposure.",
  },
  employeeVisibility: {
    label: "Employee visibility",
    text: "Employees see their own monthly ACA status, offer and affordability result in the employee portal; nothing about other employees. Turn it off to keep the ACA tab hidden from staff.",
  },

  // added: dashboard/employees
  enrolledMonth: {
    label: "Enrolled",
    text: "Enrolled means the employee was covered under the offered plan every day of the month, reported as Line 16 code 2C. Enrollment for part of a month does not count for that month.",
  },
  measurementAverage: {
    label: "Measurement-period average",
    text: "Under the look-back method the month's status comes from the average hours over the measurement period, not from this month's own hours. The number shown is that average.",
  },
  exceptionsColumn: {
    label: "Exceptions",
    text: "Blocking exceptions (red) are missing or contradictory data that stop form generation. Warnings (grey) flag provisional data or penalty risk but do not block. Open the employee to see each one and where to fix it.",
  },
  recalculate: {
    label: "Recalculate",
    text: "Runs the employee-by-month engine again from current data and overwrites the draft calculation. Finalized years are never overwritten; recalculating a finalized year opens a new version.",
  },

  // added: forms/history
  ssnMasked: {
    label: "Social security number",
    text: "Shown masked on screen; the generated PDF carries the full SSN required by the IRS.",
  },
  ageOnJan1: {
    label: "Employee's age on January 1",
    text: "Informational: the employee's age on January 1 of the tax year, used by age-rated plans to price the lowest-cost coverage.",
  },
  all12Months: {
    label: "All 12 months",
    text: "Filled only when the same value applies to every month; otherwise each month is shown.",
  },
  authoritativeTransmittal: {
    label: "Authoritative transmittal",
    text: "The one 1094-C for this employer that carries the totals; an employer files exactly one authoritative transmittal per year.",
  },
  recentActivity: {
    label: "Recent activity",
    text: "Every change to ACA configuration, overrides, recalculations and generated forms, captured automatically with who and when.",
  },
  snapshotStatus: {
    label: "Snapshot status",
    text: "Frozen: the snapshot in force. Superseded: replaced by a later re-take; kept for the record.",
  },
  enrolledMonthForms: {
    label: "Enrolled",
    text: "Covered under the offered plan every day of the month.",
  },
  monthlyStatusLegend: {
    label: "Monthly status",
    text: "One dot per month: green full-time (130+ hours), gray part-time, red hours missing, empty not employed.",
  },
} as const satisfies Record<string, GlossaryEntry>;

export type GlossaryKey = keyof typeof ACA_GLOSSARY;

/** Explanations for Line 14 / Line 16 codes, for per-code tooltips. */
export const LINE14_CODE_HELP: Record<string, string> = {
  "1A": "Qualifying Offer: minimum-value coverage to the employee at a self-only cost at or below the FPL safe harbor amount, plus at least MEC to spouse and dependents. Line 15 is left blank.",
  "1B": "Minimum-value coverage offered to the employee only.",
  "1C": "Minimum-value coverage offered to the employee and at least MEC to dependents, not to the spouse.",
  "1D": "Minimum-value coverage offered to the employee and at least MEC to the spouse, not to dependents.",
  "1E": "Minimum-value coverage offered to the employee and at least MEC to spouse and dependents.",
  "1F": "Coverage offered that does not provide minimum value.",
  "1G": "Annual code: employee was never full-time but enrolled in self-insured coverage.",
  "1H": "No offer of coverage for the full month (includes months not employed and the month employment began or ended).",
  "1J": "Minimum-value coverage to the employee; MEC conditionally offered to the spouse; nothing to dependents.",
  "1K": "Minimum-value coverage to the employee; MEC to dependents; MEC conditionally offered to the spouse.",
};

export const LINE16_CODE_HELP: Record<string, string> = {
  "2A": "Not employed on any day of the month.",
  "2B": "Not an ACA full-time employee this month, or the offer ended because employment ended during the month.",
  "2C": "Enrolled in the coverage offered for every day of the month. Takes precedence over every other code except 2E.",
  "2D": "Limited Non-Assessment Period: first partial month, waiting period for a new full-time hire, initial measurement period, or first ALE year (January to March).",
  "2E": "Multiemployer interim rule relief (union plans).",
  "2F": "Affordable under the Form W-2 wages safe harbor.",
  "2G": "Affordable under the federal poverty line safe harbor.",
  "2H": "Affordable under the rate of pay safe harbor.",
};
